import { useEffect, useRef, useState } from 'react';

/**
 * Custom cursor — gold dot that expands on interactive elements.
 * Desktop only, hidden on touch devices.
 *
 * Performance notes (Feb 2026 rewrite):
 *  - Position is updated via direct DOM `transform` writes inside a single
 *    requestAnimationFrame loop. **Never** trigger a React re-render on
 *    mousemove (that previously fired ~60–120 re-renders/sec across the
 *    whole tree and was the dominant cause of public-site mouse lag).
 *  - Hover state is tracked through *event delegation* on the document
 *    (one listener total), not by attaching listeners to every interactive
 *    element on every render — the old version re-bound hundreds of
 *    listeners per render because its effect lacked a deps array.
 *  - We toggle hover via a ref + style (no React state) too, so no
 *    re-renders happen during normal use; React only sees a re-render on
 *    enter/leave the document.
 */
const CustomCursor = () => {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const targetRef = useRef({ x: -100, y: -100 });
  const renderedRef = useRef({ x: -100, y: -100 });
  const hoveringRef = useRef(false);
  const rafRef = useRef(null);
  const [hidden, setHidden] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  // Only hide the native cursor once the custom one has actually been
  // positioned via a real mouse event. Without this gate, the user sees
  // *no* cursor at all between page load and their first mouse move —
  // because `cursor: none` fires immediately but the custom dot is
  // parked at (-100, -100) offscreen.
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    // Skip entirely on touch devices.
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) { setIsTouch(true); return undefined; }

    // Match selector for hoverable elements — used by event delegation.
    const HOVER_SEL = 'a, button, [role="button"], input, textarea, select, [data-cursor-hover]';

    const applyTransforms = () => {
      // Lerp toward the latest pointer position for a soft trail without
      // forcing a layout pass.
      const t = targetRef.current;
      const r = renderedRef.current;
      r.x += (t.x - r.x) * 0.55;
      r.y += (t.y - r.y) * 0.55;
      const ring = ringRef.current;
      const dot = dotRef.current;
      if (ring) ring.style.transform = `translate3d(${r.x}px, ${r.y}px, 0) translate(-50%, -50%)`;
      if (dot)  dot.style.transform  = `translate3d(${r.x}px, ${r.y}px, 0) translate(-50%, -50%)`;
      rafRef.current = requestAnimationFrame(applyTransforms);
    };

    let primedYet = false;
    const onMove = (e) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
      if (!primedYet) {
        // Snap the rendered position straight to the real cursor so the
        // very first frame paints in the right spot, then enable the
        // global `cursor: none` style.
        renderedRef.current.x = e.clientX;
        renderedRef.current.y = e.clientY;
        primedYet = true;
        setPrimed(true);
      }
    };
    const onEnter = () => setHidden(false);
    const onLeave = () => setHidden(true);

    // Event delegation for hover state — a single document listener that
    // walks the closest matching ancestor. Far cheaper than attaching to
    // every interactive element.
    const onOver = (e) => {
      const isHover = !!(e.target && e.target.closest && e.target.closest(HOVER_SEL));
      if (isHover === hoveringRef.current) return;
      hoveringRef.current = isHover;
      const ring = ringRef.current;
      const dot = dotRef.current;
      if (ring) {
        ring.style.width = isHover ? '48px' : '32px';
        ring.style.height = isHover ? '48px' : '32px';
      }
      if (dot) {
        dot.style.width = isHover ? '8px' : '5px';
        dot.style.height = isHover ? '8px' : '5px';
      }
    };

    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseenter', onEnter);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseover', onOver, { passive: true });
    rafRef.current = requestAnimationFrame(applyTransforms);

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseenter', onEnter);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseover', onOver);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (isTouch) return null;

  return (
    <>
      {/* Outer ring */}
      <div
        ref={ringRef}
        className="pointer-events-none fixed top-0 left-0 z-[99999] rounded-full border border-[#C4A36B]/40 mix-blend-difference hidden lg:block"
        style={{
          width: 32,
          height: 32,
          willChange: 'transform, width, height',
          transition: 'width 0.25s cubic-bezier(0.16,1,0.3,1), height 0.25s cubic-bezier(0.16,1,0.3,1), opacity 0.2s',
          opacity: hidden ? 0 : 1,
        }}
        data-testid="custom-cursor-ring"
      />
      {/* Inner dot */}
      <div
        ref={dotRef}
        className="pointer-events-none fixed top-0 left-0 z-[99999] rounded-full bg-[#C4A36B] hidden lg:block"
        style={{
          width: 5,
          height: 5,
          willChange: 'transform, width, height',
          transition: 'width 0.2s ease, height 0.2s ease, opacity 0.2s',
          opacity: hidden ? 0 : 1,
        }}
        data-testid="custom-cursor-dot"
      />
      <style>{`
        @media (min-width: 1024px) {
          ${primed ? '*, a, button, [role="button"] { cursor: none !important; }' : ''}
        }
      `}</style>
    </>
  );
};

export default CustomCursor;
